import { Injectable } from '@angular/core';
import { IMetaProprety, IEntityMetaData, IPropretyAttribute } from '../_models/IEntityMetaData';
import { ValidationError, ValidationResult, ValidationState } from '../_models/IValidationError';


@Injectable({
  providedIn: 'root'
})
export class GenericValidationService {

  validateProperty(propertyName: string, value: any, propertyDescriptor: IMetaProprety, entity?: any, isCreation?: boolean): ValidationResult {
    const errors: ValidationError[] = [];

    // Validation Required
    const isRequired = this.isFieldRequired(propertyDescriptor, isCreation);
    if (isRequired && this.isEmptyValue(value)) {
      errors.push({
        property: propertyName,
        message: propertyDescriptor.RequiredErrorMessage || `${propertyDescriptor.PropretyDisplayName} field is required.`,
        value
      });
      return { isValid: false, errors };
    }
    const attributes = propertyDescriptor.PropretyAttributes || [];

    if (attributes.includes(IPropretyAttribute.CrossValidation)) {
      if (entity) {
        this.validateCrossField(propertyName, value, propertyDescriptor, entity, errors);
      }
    }

    // Si la valeur est vide et non requise, pas de validation supplementaire
    else if (this.isEmptyValue(value)) {
      return { isValid: true, errors };
    }

    // Validation par pattern regex
    if (propertyDescriptor.ValidationPattern) {
      this.validatePattern(propertyName, value, propertyDescriptor, errors);
    }
    else if (propertyDescriptor.PropretyType) {   // Validation par type (Fall-Back)
      this.validateByType(propertyName, value, propertyDescriptor, errors);
    }



    // Validation par longueur (pour les strings)
    //if (typeof value === 'string') {
    //  this.validateStringLength(propertyName, value, propertyDescriptor, errors);
    //}

    // Validation par valeur min/max (pour les numbers)
    if (typeof value === 'number') {
      this.validateNumberRange(propertyName, value, propertyDescriptor, errors);
    }



    return { isValid: errors.length === 0, errors };
  }

  /**
   * Validation par rapport à un autre champ
   * Utilise le paramètre CrossValidationFieldName pour comparer avec un autre champ
   */
  private validateCrossField(
    propertyName: string,
    value: any,
    propertyDescriptor: IMetaProprety,
    entity: any,
    errors: ValidationError[]
  ): void {
    // Si CrossValidationFieldName est défini, comparer les valeurs
    if (propertyDescriptor.CrossValidationFieldName) {
      const targetFieldName = propertyDescriptor.CrossValidationFieldName;
      const targetValue = entity[targetFieldName];

      // Vérifier si les valeurs sont différentes
      const normalize = (v: any) => (v === null || v === undefined || v === "" ? null : v);

      if (normalize(value) !== normalize(targetValue)) {
        // if (value !== targetValue) {
        const errorMessage = propertyDescriptor.ValidationErrorMessage ||
          `${propertyDescriptor.PropretyDisplayName} must match ${targetFieldName}.`;

        errors.push({
          property: propertyName,
          message: errorMessage,
          value
        });
      }
    }
  }



  validateEntity<T>(
    entity: T,
    entityMetaData: IEntityMetaData
  ): ValidationResult {
    const errors: ValidationError[] = [];

    // Auto-détection si c'est une création en vérifiant l'ID

    const isCreation = this.isEntityCreation(entity, entityMetaData);


    Object.keys(entityMetaData.PropretyDescriptors).forEach(propertyName => {
      const propertyDescriptor = entityMetaData.PropretyDescriptors[propertyName];
      const value = (entity as any)[propertyName];

      const propertyValidation = this.validateProperty(propertyName, value, propertyDescriptor, entity, isCreation);
      if (!propertyValidation.isValid) {
        errors.push(...propertyValidation.errors);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Détermine si l'entité est en création en vérifiant son ID
   * (ID = 0, null ou undefined = création)
   */
  private isEntityCreation<T>(entity: T, entityMetaData: IEntityMetaData): boolean {
    // Trouver la propriété qui a l'attribut KeyProperty
    const keyPropertyName = Object.keys(entityMetaData.PropretyDescriptors).find(propertyName => {
      const descriptor = entityMetaData.PropretyDescriptors[propertyName];
      return descriptor.PropretyAttributes?.includes(IPropretyAttribute.KeyProperty);
    });

    // Si on trouve la propriété Key, vérifier sa valeur
    if (keyPropertyName) {
      const idValue = (entity as any)[keyPropertyName];
      return idValue === 0 || idValue === null || idValue === undefined;
    }

    // Par défaut, considérer comme une mise à jour si pas de Key trouvée
    return false;
  }

  /**
   * Détermine si un champ est requis selon le contexte (création ou mise à jour)
   */
  private isFieldRequired(propertyDescriptor: IMetaProprety, isCreation?: boolean): boolean {
    const attributes = propertyDescriptor.PropretyAttributes || [];

    // Requis dans tous les cas
    if (attributes.includes(IPropretyAttribute.Required)) {
      return true;
    }

    // Requis uniquement en création
    if (isCreation === true && attributes.includes(IPropretyAttribute.RequiredOnlyCreation)) {
      return true;
    }

    // Requis uniquement en mise à jour
    if (isCreation === false && attributes.includes(IPropretyAttribute.RequiredOnlyUpdate)) {
      return true;
    }

    return false;
  }

  private isEmptyValue(value: any): boolean {
    return value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'number' && isNaN(value));
  }

  private validateByType(propertyName: string, value: any, propertyDescriptor: IMetaProprety, errors: ValidationError[]): void {
    const type = propertyDescriptor.PropretyType;

    switch (type) {
      case 'Alphabetic':
        if (!/^[a-zA-ZÀ-ÿ\s]*$/.test(value)) {
          errors.push({
            property: propertyName,
            message: propertyDescriptor.ValidationErrorMessage || `The field ${propertyDescriptor.PropretyDisplayName} must contain only letters.`,
            value
          });
        }
        break;

      case 'Alphanumeric':
        if (!/^[a-zA-Z0-9]*$/.test(value)) {
          errors.push({
            property: propertyName,
            message: propertyDescriptor.ValidationErrorMessage || `The field ${propertyDescriptor.PropretyDisplayName} must be alphanumeric.`,
            value
          });
        }
        break;

      case 'Email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.push({
            property: propertyName,
            message: propertyDescriptor.ValidationErrorMessage || `The field ${propertyDescriptor.PropretyDisplayName} must be a valid email.`,
            value
          });
        }
        break;

      case 'Numeric':
        if (isNaN(Number(value))) {
          errors.push({
            property: propertyName,
            message: propertyDescriptor.ValidationErrorMessage || `The field ${propertyDescriptor.PropretyDisplayName} must be a number.`,
            value
          });
        }
        break;
    }
  }

  private validatePattern(
    propertyName: string,
    value: any,
    propertyDescriptor: IMetaProprety,
    errors: ValidationError[]
  ): void {
    try {
      const pattern = new RegExp(propertyDescriptor.ValidationPattern!);
      if (!pattern.test(value)) {
        errors.push({
          property: propertyName,
          message: propertyDescriptor.ValidationErrorMessage || `Field with invalide Format.`,
          value
        });
      }
    } catch (e) {
      console.error(`>>> Regex pattern invalide pour ${propertyName}:`, propertyDescriptor.ValidationPattern);
    }
  }

  private validateStringLength(
    propertyName: string,
    value: string,
    propertyDescriptor: IMetaProprety,
    errors: ValidationError[]
  ): void {
    const length = value.length;

    if (propertyDescriptor.MinLength !== undefined && length < propertyDescriptor.MinLength) {
      errors.push({
        property: propertyName,
        message: propertyDescriptor.ValidationErrorMessage || `Field ${propertyDescriptor.PropretyDisplayName} must have at least ${propertyDescriptor.MinLength} characters.`,
        value
      });
    }

    if (propertyDescriptor.MaxLength !== undefined && length > propertyDescriptor.MaxLength) {
      errors.push({
        property: propertyName,
        message: propertyDescriptor.ValidationErrorMessage || `Field ${propertyDescriptor.PropretyDisplayName} must have a maximum of ${propertyDescriptor.MinLength} characters.`,
        value
      });
    }
  }

  private validateNumberRange(
    propertyName: string,
    value: number,
    propertyDescriptor: IMetaProprety,
    errors: ValidationError[]
  ): void {
    if (propertyDescriptor.MinLength !== undefined && value < propertyDescriptor.MinLength) {
      errors.push({
        property: propertyName,
        message: propertyDescriptor.ValidationErrorMessage || `Field ${propertyDescriptor.PropretyDisplayName} must be greater than ${propertyDescriptor.MinLength}.`,
        value
      });
    }

    if (propertyDescriptor.MaxLength !== undefined && value > propertyDescriptor.MaxLength) {
      errors.push({
        property: propertyName,
        message: propertyDescriptor.ValidationErrorMessage || `Field ${propertyDescriptor.PropretyDisplayName} must be less than ${propertyDescriptor.MaxLength}.`,
        value
      });
    }
  }

  convertValidationResultToState(validationResult: ValidationResult): ValidationState {
    const state: ValidationState = {};

    validationResult.errors.forEach(error => {
      if (!state[error.property]) {
        state[error.property] = [];
      }
      state[error.property].push(error.message);
    });

    return state;
  }
}
